/**
 * Exits by role: the roles people left over the financial year to date.
 *
 * Three readings this panel protects:
 *
 *  1. A ROLE WITH FEWER THAN FIVE EXITS IS NEVER NAMED. Naming it would identify the people who
 *     left it, so identity folds every such role, with the exits from positions that held no
 *     title, into one "other roles" total as it serves the block. The page never unpicks it.
 *  2. A WITHHELD COUNT IS NOT AN EMPTY ONE. A count withheld to protect a small group arrives
 *     as null: the row stays, the count shows as a dash with the rule attached, and it never
 *     reaches the scale as a zero.
 *  3. TEMPORARY AND SEASONAL ROLES ARE SHOWN APART. Marking, invigilation, moderation,
 *     tutoring, enrolment support and internships are engaged per session or per task, so an
 *     exit there is the end of an engagement, not attrition. The list is fixed, so its roles
 *     show whether or not anyone left.
 *
 * There is no rate: the 1 August rename retitled the positions most leavers came from, so a
 * title held on the day of leaving has no stable headcount to divide by. A copy stored before
 * the daily recalculation still carries one; only its roles and counts are shown, and a role
 * whose count it withholds is not named.
 *
 * Window: the financial year to date. It follows neither the period picker nor the basis
 * toggle, and it is not annualised.
 */
import React, { useMemo, type ReactNode } from 'react';
import { BarRow } from './BarRow';
import { PanelShell, PanelSkeleton, PanelUnavailable } from './PanelShell';
import {
  Def,
  INK,
  fmtDay,
  fmtNum,
  panelDateLabel,
  readExtras,
  type DefLookup,
  type ExitRoleRow,
  type WorkforceBasis,
  type WorkforcePanels,
} from './apiContract';

interface Props {
  panels: WorkforcePanels | null;
  /** Part of the shared panel signature. Exits by role follow neither the basis toggle nor the
   *  period picker: every exit in the window is counted, which is what the date line says. */
  basis: WorkforceBasis;
  loading: boolean;
  def: DefLookup;
}

const ROLE_COLOR = INK;
const OTHER_COLOR = '#B9C4D6';
const SEASONAL_COLOR = '#CFAD80';

const SUPPRESSION_NOTE =
  'Withheld: fewer than 5 people, or a figure that would let a smaller one elsewhere on this page '
  + 'be worked out. It is not zero, and it is still in the totals.';

const SEASONAL_COPY =
  'Marking, invigilation, moderation, tutoring, enrolment support and internships are engaged per '
  + 'session or per task, so an exit here is the end of an engagement, not attrition.';

/** Panel-local definitions. Anything /metric-definitions publishes for the same key wins; these
 *  only fill the gap. */
const OWN_DEFS: Record<string, ReactNode> = {
  exit_roles: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Exits by role</p>
      <p>
        Exits over the window, grouped by the role title held on the day of leaving. People who
        left on a contract extension or renewal code are not counted as exits, as in the Remco data
        pack, so they are not here either.
      </p>
      <ul className="list-disc space-y-0.5 pl-3.5 text-[11px] text-amber-200/90">
        <li>
          Roles with fewer than five exits are not named: naming the role would identify the people
          who left it.
        </li>
        <li>The window is the financial year to date and is not annualised.</li>
      </ul>
    </div>
  ),
  exit_roles_leavers: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Exits</p>
      <p>People who left from this role over the window.</p>
    </div>
  ),
  exit_roles_other: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Other roles</p>
      <p>
        Every role with fewer than 5 exits, counted together so that no one who left is identified
        by their role, with the exits from positions that held no title.
      </p>
    </div>
  ),
  exit_roles_seasonal: (
    <div className="space-y-1.5">
      <p className="font-semibold text-white">Temporary and seasonal roles</p>
      <p>{SEASONAL_COPY}</p>
    </div>
  ),
};

/** A withheld figure: a dash with the rule attached, never 0. */
const Withheld: React.FC<{ def: DefLookup }> = ({ def }) => (
  <span className="inline-flex items-center justify-end text-gray-400">
    —<span className="sr-only">withheld</span>
    <Def def={def} k="suppression" text={SUPPRESSION_NOTE} />
  </span>
);

const NotAvailable: React.FC<{ children: ReactNode }> = ({ children }) => (
  <div className="rounded border border-gray-200 bg-gray-50 p-2.5 text-[11px] leading-snug text-gray-600">
    {children}
  </div>
);

/**
 * The words that date this panel, from the block's own provenance entry, never the frame's.
 * A block stamped with a kind reads through panelDateLabel. A copy stored before blocks were
 * stamped carries no kind, and panelDateLabel would call its window a point in time, so it is
 * dated by the day it runs to. null: nothing to date it by.
 */
function dateWords(panels: WorkforcePanels | null, block: string): string | null {
  const entry = panels?.panels_provenance?.[block];
  if (!entry?.computed_for) return null;
  if (entry.kind) return panelDateLabel(panels, block).replace(/^To /, 'to ');
  return `to ${fmtDay(entry.computed_for)}`;
}

const isWithheld = (r: ExitRoleRow) => r.suppressed || r.leavers === null;

export const ExitRolesPanel: React.FC<Props> = ({ panels, loading, def }) => {
  const defs = useMemo<DefLookup>(() => (k: string) => def(k) ?? OWN_DEFS[k], [def]);

  const block = readExtras(panels, 'exit_roles');
  const current = block && !Array.isArray(block) ? block : null;
  const stored = Array.isArray(block) ? block : null;
  const hasRoles = !!current || !!stored?.length;

  const named: ExitRoleRow[] = current
    ? current.roles
    : (stored ?? []).filter((r) => !isWithheld(r)).sort((a, b) => (b.leavers ?? 0) - (a.leavers ?? 0));
  const storedWithheld = stored ? stored.filter(isWithheld).length : 0;
  const seasonal = current?.seasonal ?? null;
  const other = current?.other_roles ?? null;
  const showOther = !!other && (other.roles > 0 || other.leavers !== 0);

  // Withheld counts are excluded from the scale outright: a null must never reach Math.max as
  // a 0 and quietly stretch every other bar.
  const max = Math.max(
    0,
    ...[
      ...named.map((r) => r.leavers),
      other && showOther ? other.leavers : null,
      ...(seasonal?.roles ?? []).map((r) => r.leavers),
    ].filter((v): v is number => typeof v === 'number'),
  );
  const anyWithheld =
    [...named, ...(seasonal?.roles ?? [])].some(isWithheld)
    || storedWithheld > 0
    || (!!other && showOther && other.leavers === null)
    || seasonal?.leavers === null
    || current?.true_exits === null;

  const dated = dateWords(panels, 'exit_roles');
  const windowNote = `${
    dated ? `${dated.charAt(0).toUpperCase()}${dated.slice(1)}, financial year to date` : 'Financial year to date'
  }, not annualised. Titles are as held on the day of leaving. Follows neither the period picker nor the basis toggle.`;

  const row = (r: ExitRoleRow, color: string) => {
    const withheld = isWithheld(r);
    return (
      <BarRow
        key={r.role}
        label={r.role}
        value={withheld ? null : r.leavers}
        max={max}
        color={color}
        labelWidth={300}
        valueText={withheld ? <Withheld def={defs} /> : fmtNum(r.leavers)}
      />
    );
  };

  const footnote = (
    <>
      {current
        ? typeof current.true_exits === 'number'
          ? `${fmtNum(current.true_exits)} exits in all over the window: the roles named, the other roles and the temporary and seasonal roles together. `
          : 'The number of exits in all is withheld to protect small groups. '
        : ''}
      People who left on a contract extension or renewal code are not counted as exits, so they are
      not here either.
      {anyWithheld ? (
        <span className="text-amber-700">
          {' '}
          A dash is a count withheld to protect small groups: it is not zero, and it is still in the
          totals.
        </span>
      ) : null}
    </>
  );

  return (
    <PanelShell
      title="Exits by role"
      defKey="exit_roles"
      def={defs}
      window={windowNote}
      footnote={hasRoles ? footnote : undefined}
    >
      {loading && !panels ? (
        <PanelSkeleton height={240} />
      ) : !panels ? (
        <PanelUnavailable what="Exits by role" />
      ) : !hasRoles ? (
        <NotAvailable>Exits by role are not available for this date.</NotAvailable>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              <span>Role held on the day of leaving</span>
              <span className="inline-flex items-center">
                Exits
                <Def def={defs} k="exit_roles_leavers" />
              </span>
            </div>

            {named.map((r) => row(r, ROLE_COLOR))}

            {current && !named.length ? (
              <p className="py-1 text-[11px] text-slate-400">
                No role had {fmtNum(current.ranking_floor)} or more exits over the window.
              </p>
            ) : null}

            {current && other && showOther ? (
              <BarRow
                label={
                  <span className="inline-flex max-w-full items-center">
                    <span className="truncate">
                      {other.roles > 0
                        ? `Other roles (${fmtNum(other.roles)} ${other.roles === 1 ? 'role' : 'roles'}, fewer than ${fmtNum(current.ranking_floor)} exits each)`
                        : 'Positions with no title'}
                    </span>
                    <Def def={defs} k="exit_roles_other" />
                  </span>
                }
                value={other.leavers}
                max={max}
                color={OTHER_COLOR}
                labelWidth={300}
                valueText={other.leavers === null ? <Withheld def={defs} /> : fmtNum(other.leavers)}
              />
            ) : null}

            {storedWithheld ? (
              <BarRow
                label={`Other roles (${fmtNum(storedWithheld)} ${storedWithheld === 1 ? 'role' : 'roles'}, counts withheld)`}
                value={null}
                max={max}
                color={OTHER_COLOR}
                labelWidth={300}
                valueText={<Withheld def={defs} />}
              />
            ) : null}
          </div>

          {seasonal ? (
            <div>
              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                <span className="inline-flex items-center">
                  Temporary and seasonal roles, shown apart
                  <Def def={defs} k="exit_roles_seasonal" />
                </span>
                <span className="tabular-nums">
                  {seasonal.leavers === null ? <Withheld def={defs} /> : `${fmtNum(seasonal.leavers)} exits`}
                </span>
              </div>
              {seasonal.roles.map((r) => row(r, SEASONAL_COLOR))}
              <p className="pt-1 text-[11px] leading-snug text-slate-400">{SEASONAL_COPY}</p>
            </div>
          ) : null}
        </div>
      )}
    </PanelShell>
  );
};
